import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { vendorQueries } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { T, sans, card, th, td, linkBtn } from './VendorDetails';

type Status = 'active' | 'not_detected' | 'removed';

interface Product {
  id: string;
  product: string;
  category: string;
  description: string | null;
  source: 'detected' | 'manual';
  status: Status;
  evidence: string[];
  notes: string | null;
  first_detected: string | null;
  last_seen: string | null;
}

interface FourthParty {
  company_name: string;
  company_domain: string;
  products: Product[];
  active_products: number;
  categories: string[];
  first_detected: string | null;
  monitored_vendor: { id: string; name: string; score: number | null; risk_level: string | null } | null;
  // Set only when not a monitored_vendor -- a scan of this domain (e.g. from "Run risk scan")
  // has completed under this tenant. Same "informational only" status as monitored_vendor.
  latest_scan: { scan_id: string; score: number | null; risk_level: string | null; completed_at: string } | null;
}

interface Payload {
  fourth_parties: FourthParty[];
  summary: { fourth_parties: number; products: number; inactive_products: number; by_category: Record<string, number> };
  last_check: { checked_at: string; hosts_checked: number; signals_failed: string[]; new_count: number; gone_count: number } | null;
  detecting: boolean;
  stale: boolean;
  categories: string[];
  changes?: { new: number; gone: number } | null;
}

interface CatalogEntry {
  product: string;
  company_name: string;
  company_domain: string;
  category: string;
  description: string;
}

interface Props {
  vendorId: string;
  vendorName: string;
  isDark: boolean;
}

const emptyForm = { company_name: '', company_domain: '', product: '', category: 'Other', description: '', notes: '' };

const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};

const fmtDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const fmtDateTime = (value: string) =>
  new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const fourthPartiesKey = (vendorId: string) => ['vendors', 'detail', vendorId, 'fourth-parties'] as const;

/**
 * What could not be read in the last check, in plain words. `optional` sources (public datasets that are often
 * slow or down) are extras: their failure is shown calmly, while an unreadable website or failing DNS is a warning.
 */
const DNS_SIGNALS = ['ns', 'mx', 'txt', 'dkim', 'dmarc', 'cname', 'mailhosts', 'asn'];

const failedSignalNotes = (failed: string[]): { important: string[]; optional: string[] } => {
  const important: string[] = [];
  const optional: string[] = [];
  if (failed.includes('headers')) {
    important.push("The vendor's website could not be reached, so the services it loads could not be read.");
  } else if (failed.includes('html') || failed.includes('scripts')) {
    important.push("The vendor's website did not return a readable page (it may block automated requests), so the scripts and services it loads could not be read.");
  }
  if (failed.includes('subhosts')) important.push("The vendor's subdomains did not answer.");
  if (failed.includes('tls')) important.push("The vendor's certificate could not be read.");
  if (failed.some(s => DNS_SIGNALS.includes(s))) {
    const names = failed.filter(s => DNS_SIGNALS.includes(s)).join(', ');
    important.push(`Some DNS lookups timed out (${names}).`);
  }
  if (failed.includes('webarchive')) optional.push("Common Crawl's public index did not answer (it is often overloaded), so whether the vendor's pages are archived there could not be checked.");
  if (failed.includes('discovery')) optional.push('The public certificate logs (crt.sh, CertSpotter) did not answer, so extra hostnames could not be looked up.');
  if (failed.includes('cloudranges')) optional.push("AWS's published address ranges could not be downloaded, so AWS regions could not be checked.");
  return { important, optional };
};

export function CompanyLogo({ name, domain, isDark, size = 28 }: { name: string; domain: string; isDark: boolean; size?: number }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    // Only the letter fallback keeps a tinted backdrop -- a bare letter with no background
    // wouldn't read as a logo placeholder. A real logo image is shown on its own, no box.
    return (
      <span style={{
        width: size, height: size, borderRadius: size >= 36 ? 9 : 6, flexShrink: 0,
        background: isDark ? '#1a1a1a' : T.surface2, border: `1px solid ${isDark ? '#1f1f1f' : T.bdr}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 700, color: isDark ? '#94a3b8' : T.slate,
      }}>
        {(name || '?').trim().charAt(0).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`} alt="" onError={() => setFailed(true)}
      style={{ width: size, height: size, flexShrink: 0, objectFit: 'contain' }}
    />
  );
}

// A small, consistent hue per category (hashed from the name) so a product's category reads at a
// glance across the table/cards, the way BuiltWith/Wappalyzer-style tech-stack views color-code
// categories -- reuses the same `${color}14`/`${color}55` tinting the existing `pill()` helper
// already does, just with a wider, deliberately-picked palette instead of always slate.
const CATEGORY_PALETTE = ['#3B82F6', '#16A34A', '#D97706', '#7C3AED', '#DB2777', '#0D9488', '#4F46E5', '#059669'];
export function categoryColor(category: string): string {
  if (!category || category === 'Other') return '#64748B';
  let hash = 0;
  for (let i = 0; i < category.length; i++) hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  return CATEGORY_PALETTE[hash % CATEGORY_PALETTE.length];
}

// Categories whose providers receive the vendor's data or money (mail, analytics, payments, support chat, business apps).
const DATA_CATEGORIES = new Set(['Email & Messaging', 'Analytics & Tracking', 'Payments', 'Customer Support & Chat', 'Business Applications']);
const NEW_DAYS = 7;
type Tab = 'active' | 'not_detected' | 'removed';
type SortKey = 'shared' | 'name' | 'products' | 'newest';
const csvCell = (v: unknown) => {
  let s = v == null ? '' : String(v);
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const isNew = (iso: string | null) => !!iso && Date.now() - new Date(iso).getTime() < NEW_DAYS * 86400000;

export default function VendorFourthParties({ vendorId, vendorName, isDark }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = !!user && user.role !== 'viewer';
  const canRemove = !!user && (user.role === 'admin' || user.role === 'super_admin');

  const queryClient = useQueryClient();
  const fpKey = fourthPartiesKey(vendorId);
  const [view, setView] = useState<'company' | 'category' | 'map'>('company');
  const [tab, setTab] = useState<Tab>('active');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('shared');
  const [openDomain, setOpenDomain] = useState<string | null>(null);
  const [evidenceOpen, setEvidenceOpen] = useState<Set<string>>(new Set());
  const [checkNote, setCheckNote] = useState('');
  const [checkError, setCheckError] = useState('');
  const [statusDismissed, setStatusDismissed] = useState(false);

  // dialog: null = closed, 'new' = adding, a product = editing it
  const [editing, setEditing] = useState<{ product: Product; party: FourthParty } | 'new' | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogResults, setCatalogResults] = useState<CatalogEntry[]>([]);
  const [removing, setRemoving] = useState<{ products: Product[]; party: FourthParty } | null>(null);
  const [removeError, setRemoveError] = useState('');
  const [rowError, setRowError] = useState('');

  const autoChecked = useRef<string | null>(null);

  const muted = isDark ? '#94a3b8' : T.muted;
  const slate = isDark ? '#94a3b8' : T.slate;
  const ink = isDark ? '#e2e8f0' : T.ink;
  const line = isDark ? '#1f1f1f' : T.bdr;
  const line2 = isDark ? '#161616' : '#F1F5F9';
  const accent = isDark ? '#60A5FA' : '#185FA5';
  const accentBg = isDark ? 'rgba(59,130,246,.14)' : '#E6F1FB';
  const accentInk = isDark ? '#BFDBFE' : '#0C447C';
  const subtle = isDark ? '#141414' : T.surface2;
  const surface = isDark ? '#0d0d0d' : T.surface;
  const danger = isDark ? '#F87171' : T.red;
  const orange = '#EA580C';

  const checkMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/v1/vendors/${id}/fourth-parties/detect`)).data as Payload,
    // The check answers with the refreshed list itself, so it goes straight into the cache (no refetch needed).
    onSuccess: (payload, id) => queryClient.setQueryData(fourthPartiesKey(id), payload),
  });
  const checking = checkMutation.isPending;

  const fpQuery = useQuery({
    queryKey: fpKey,
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/fourth-parties`)).data as Payload,
    // While a check started elsewhere (a scan that just finished) is still running, keep refreshing.
    refetchInterval: query => (query.state.data?.detecting && !checking ? 3000 : false),
  });
  const data = fpQuery.data ?? null;
  const loading = !data && (fpQuery.isPending || fpQuery.isFetching);
  const loadError = !data && fpQuery.isError ? apiError(fpQuery.error, "Couldn't load the fourth parties. Try again.") : '';

  // Which of the tenant's other vendors depend on the same company (the portfolio concentration view's data).
  const portfolioQuery = useQuery(vendorQueries.fourthPartiesPortfolio());
  const sharedWith = useMemo(() => {
    const m = new Map<string, { id: string; name: string; domain: string }[]>();
    ((portfolioQuery.data as any)?.companies || []).forEach((c: any) => {
      const others = (c.vendors || []).filter((v: any) => v.id !== vendorId);
      if (others.length) m.set(String(c.company_domain).toLowerCase(), others);
    });
    return m;
  }, [portfolioQuery.data, vendorId]);
  const sharedOf = (party: FourthParty) => sharedWith.get(party.company_domain.toLowerCase()) || [];

  const invalidatesFourthParties = { invalidates: [fpKey, ['vendors', 'fourth-parties-portfolio']] };
  const saveMutation = useMutation({
    mutationFn: ({ target, body }: { target: 'new' | Product; body: typeof emptyForm }) => (target === 'new'
      ? api.post(`/v1/vendors/${vendorId}/fourth-parties`, body)
      : api.put(`/v1/vendors/${vendorId}/fourth-parties/${target.id}`, body)),
    meta: invalidatesFourthParties,
  });
  const removeMutation = useMutation({
    mutationFn: async (products: Product[]) => { for (const p of products) await api.delete(`/v1/vendors/${vendorId}/fourth-parties/${p.id}`); },
    meta: invalidatesFourthParties,
  });
  const restoreMutation = useMutation({
    mutationFn: (product: Product) => api.post(`/v1/vendors/${vendorId}/fourth-parties/${product.id}/restore`),
    meta: invalidatesFourthParties,
  });
  const saving = saveMutation.isPending || removeMutation.isPending;

  const runCheck = async () => {
    setCheckError('');
    setCheckNote('');
    setStatusDismissed(false);
    try {
      const payload = await checkMutation.mutateAsync(vendorId);
      const changes = payload.changes;
      if (changes) {
        const parts = [];
        if (changes.new) parts.push(`${changes.new} new`);
        if (changes.gone) parts.push(`${changes.gone} no longer seen`);
        setCheckNote(parts.length ? `Check complete: ${parts.join(', ')}.` : 'Check complete: no changes.');
      }
    } catch (error) {
      setCheckError(apiError(error, "The check couldn't be completed. Try again."));
    }
  };

  useEffect(() => {
    setOpenDomain(null);
    setCheckNote('');
    setCheckError('');
  }, [vendorId]);

  // A vendor that has never been checked (onboarded before this feature, or never scanned), or whose last
  // check was made by an older detector that could see less, gets checked when someone who may run one
  // opens the tab. `autoChecked` limits this to once per vendor, so background refetches can't start it again.
  useEffect(() => {
    if (!data || !canEdit || checking || data.detecting || (data.last_check && !data.stale) || autoChecked.current === vendorId) return;
    autoChecked.current = vendorId;
    runCheck();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [data, canEdit, vendorId]);

  // Catalog search for the "add" dialog
  useEffect(() => {
    if (editing !== 'new' || catalogQuery.trim().length < 2) { setCatalogResults([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/v1/fourth-parties/catalog', { params: { q: catalogQuery.trim() } });
        if (!cancelled) setCatalogResults(res.data.results || []);
      } catch {
        if (!cancelled) setCatalogResults([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [catalogQuery, editing]);

  const busy = checking || !!data?.detecting;
  const parties = useMemo(() => data?.fourth_parties || [], [data]);

  /* ── derived ── */
  const statusCount = (s: Tab) => parties.filter(p => p.products.some(x => x.status === s)).length;
  const counts = { active: statusCount('active'), not_detected: statusCount('not_detected'), removed: statusCount('removed') };
  const activeParties = parties.filter(p => p.products.some(x => x.status === 'active'));
  const categoryCounts = useMemo(() => {
    const m = new Map<string, number>();
    activeParties.forEach(p => new Set(p.products.filter(x => x.status === 'active').map(x => x.category)).forEach(c => m.set(c, (m.get(c) || 0) + 1)));
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [parties]);
  const sharedCount = activeParties.filter(p => sharedOf(p).length > 0).length;
  const dataCount = activeParties.filter(p => p.products.some(x => x.status === 'active' && DATA_CATEGORIES.has(x.category))).length;
  const activeProducts = activeParties.reduce((n, p) => n + p.products.filter(x => x.status === 'active').length, 0);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const list = parties
      .map(party => ({ party, products: party.products.filter(p => p.status === tab) }))
      .filter(({ party, products }) => products.length > 0
        && (!category || products.some(p => p.category === category))
        && (!needle || [party.company_name, party.company_domain, ...products.flatMap(p => [p.product, p.category])].some(v => (v || '').toLowerCase().includes(needle))));
    const sorters: Record<SortKey, (a: typeof list[number], b: typeof list[number]) => number> = {
      shared: (a, b) => sharedOf(b.party).length - sharedOf(a.party).length || b.products.length - a.products.length || a.party.company_name.localeCompare(b.party.company_name),
      name: (a, b) => a.party.company_name.localeCompare(b.party.company_name),
      products: (a, b) => b.products.length - a.products.length || a.party.company_name.localeCompare(b.party.company_name),
      newest: (a, b) => new Date(b.party.first_detected || 0).getTime() - new Date(a.party.first_detected || 0).getTime(),
    };
    return list.sort(sorters[sortKey]);
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [parties, tab, category, search, sortKey, sharedWith]);

  const openParty = openDomain ? parties.find(p => p.company_domain === openDomain) || null : null;
  const openIndex = openParty ? visible.findIndex(v => v.party.company_domain === openParty.company_domain) : -1;

  const openDialog = (target: { product: Product; party: FourthParty } | 'new') => {
    setEditing(target);
    setFormError('');
    setCatalogQuery('');
    setCatalogResults([]);
    setForm(target === 'new' ? emptyForm : {
      company_name: target.party.company_name, company_domain: target.party.company_domain,
      product: target.product.product, category: target.product.category,
      description: target.product.description || '', notes: target.product.notes || '',
    });
  };

  const pickCatalogEntry = (entry: CatalogEntry) => {
    setForm(f => ({ ...f, company_name: entry.company_name, company_domain: entry.company_domain, product: entry.product, category: entry.category, description: entry.description }));
    setCatalogQuery('');
    setCatalogResults([]);
  };

  const save = async () => {
    const detectedEdit = editing !== 'new' && editing !== null && editing.product.source === 'detected';
    if (!detectedEdit) {
      if (!form.company_name.trim()) { setFormError('Enter the company name.'); return; }
      if (!form.company_domain.trim()) { setFormError("Enter the company's website domain, for example stripe.com."); return; }
    }
    setFormError('');
    try {
      if (editing) await saveMutation.mutateAsync({ target: editing === 'new' ? 'new' : editing.product, body: form });
      setEditing(null);
    } catch (error) {
      setFormError(apiError(error, "Couldn't save. Try again."));
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setRemoveError('');
    try {
      await removeMutation.mutateAsync(removing.products);
      setRemoving(null);
    } catch (error) {
      setRemoveError(apiError(error, "Couldn't remove it. Try again."));
    }
  };

  const restore = async (product: Product) => {
    setRowError('');
    try {
      await restoreMutation.mutateAsync(product);
    } catch (error) {
      setRowError(apiError(error, "Couldn't restore it. Try again."));
    }
  };

  const exportCsv = () => {
    const head = ['Company', 'Domain', 'Product', 'Category', 'Status', 'Source', 'First detected', 'Last seen', 'Shared with your vendors', 'Note'];
    const rows = parties.flatMap(party => party.products.map(p => [party.company_name, party.company_domain, p.product, p.category,
      p.status === 'active' ? 'Active' : p.status === 'not_detected' ? 'No longer seen' : 'Removed', p.source === 'manual' ? 'Added manually' : 'Detected',
      p.first_detected ? fmtDate(p.first_detected) : '', p.last_seen ? fmtDate(p.last_seen) : '', sharedOf(party).map(v => v.name).join('; '), p.notes || '']));
    const body = '﻿' + [head, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8' }));
    a.download = `${(vendorName || 'vendor').replace(/[^\w-]+/g, '_')}_fourth_parties.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };

  /* ── styles ─────────────────────────────────────────────────────── */
  const pill = (color: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 99, whiteSpace: 'nowrap',
    color, background: `${color}${isDark ? '26' : '14'}`,
  });
  const btn = (kind: 'default' | 'primary' | 'ghost' | 'danger' = 'default'): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans, whiteSpace: 'nowrap',
    ...(kind === 'primary' ? { border: '1px solid #185FA5', background: '#185FA5', color: '#fff' }
      : kind === 'ghost' ? { border: '1px solid transparent', background: 'transparent', color: muted }
      : kind === 'danger' ? { border: `1px solid ${line}`, background: surface, color: danger }
      : { border: `1px solid ${line}`, background: surface, color: ink }),
  });
  const segBtn = (on: boolean): React.CSSProperties => ({
    padding: '6px 11px', fontSize: 12, fontWeight: 600, fontFamily: sans, cursor: 'pointer', border: 'none',
    background: on ? ink : 'transparent', color: on ? surface : slate, whiteSpace: 'nowrap',
  });
  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, fontFamily: sans, color: ink,
    background: surface, border: `1px solid ${line}`, borderRadius: 7, outline: 'none',
  };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 600, color: slate, marginBottom: 5 };
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400, padding: 16,
  };
  const dialog: React.CSSProperties = {
    width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', background: surface,
    border: `1px solid ${line}`, borderRadius: 10, padding: 20, fontFamily: sans,
  };
  const errorText: React.CSSProperties = { fontSize: 12, color: danger };
  const thS: React.CSSProperties = { ...th(isDark), whiteSpace: 'nowrap' };
  const tdS: React.CSSProperties = { ...td(isDark), padding: '12px 14px', verticalAlign: 'middle' };

  const sourceTag = (party: FourthParty, products: Product[]) => {
    if (tab === 'not_detected') return <span style={pill(isDark ? '#FBBF24' : '#B45309')}>No longer seen</span>;
    if (tab === 'removed') return <span style={pill(muted)}>Removed</span>;
    return products.every(p => p.source === 'manual') ? <span style={pill('#7C3AED')}>Added manually</span> : <span style={{ fontSize: 12, color: muted }}>Detected</span>;
  };
  const SharedCell = ({ party }: { party: FourthParty }) => {
    const n = sharedOf(party).length;
    return n ? <span style={pill(orange)} title={sharedOf(party).map(v => v.name).join(', ')}>{n} vendor{n === 1 ? '' : 's'}</span>
      : <span style={{ fontSize: 12, color: muted }}>Only this vendor</span>;
  };

  const detectedEdit = editing !== null && editing !== 'new' && editing.product.source === 'detected';
  const lastCheck = data?.last_check;
  const hasAny = parties.length > 0;
  const status = !busy && lastCheck && lastCheck.signals_failed.length > 0 && !statusDismissed ? failedSignalNotes(lastCheck.signals_failed) : null;

  return (
    <div style={{ display: 'grid', gap: 12, fontFamily: sans }}>
      <style>{`
        @keyframes fpDrawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .fp-row:hover td { background: ${subtle}; }
        @media (max-width: 1100px) { .fp-kpis { grid-template-columns: minmax(0,1fr) !important; } .fp-kpis > div { border-right: none !important; border-bottom: 1px solid ${line2}; } }
      `}</style>

      {/* 1 Header */}
      <div style={{ ...card(isDark), display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: ink }}>Fourth parties</div>
          <div style={{ fontSize: 12, color: slate, marginTop: 3, lineHeight: 1.55 }}>
            Companies and services {vendorName} depends on, found from its public DNS, website, scripts and certificates
            {' · '}{busy ? 'checking public records…' : lastCheck ? `last checked ${fmtDateTime(lastCheck.checked_at)}` : 'not checked yet'}
            {!busy && lastCheck && lastCheck.new_count > 0 && <> <span style={pill(accent)}>{lastCheck.new_count} new</span></>}
            {!busy && lastCheck && lastCheck.gone_count > 0 && <> <span style={pill(muted)}>{lastCheck.gone_count} no longer seen</span></>}
          </div>
        </div>
        <button style={{ ...btn(), opacity: hasAny ? 1 : 0.5 }} disabled={!hasAny} onClick={exportCsv}>↓ Export CSV</button>
        {canEdit && <button style={btn()} onClick={() => openDialog('new')}>+ Add manually</button>}
        {canEdit && <button style={{ ...btn('primary'), opacity: busy ? 0.7 : 1 }} onClick={runCheck} disabled={busy}>{busy ? 'Checking…' : '⟳ Check now'}</button>}
      </div>

      {(checkNote || checkError || rowError) && (
        <div role={checkError || rowError ? 'alert' : 'status'} style={{ fontSize: 12, color: checkError || rowError ? danger : (isDark ? '#4ADE80' : T.grn) }}>
          {checkError || rowError || checkNote}
        </div>
      )}

      {loading ? (
        <div style={{ ...card(isDark), textAlign: 'center', padding: '40px 0', fontSize: 12.5, color: muted }}>Loading fourth parties…</div>
      ) : loadError ? (
        <div style={{ ...card(isDark), textAlign: 'center', padding: '40px 0', fontSize: 12.5, color: danger }}>
          {loadError} <button style={linkBtn(isDark)} onClick={() => fpQuery.refetch()}>Retry</button>
        </div>
      ) : !hasAny ? (
        <div style={{ ...card(isDark), textAlign: 'center', padding: '48px 20px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: ink, marginBottom: 4 }}>{busy ? 'Looking for fourth parties…' : 'No fourth parties found yet'}</div>
          <div style={{ fontSize: 12.5, color: slate, maxWidth: 440, margin: '0 auto', lineHeight: 1.55 }}>
            {busy ? `Reading ${vendorName}'s DNS, mail and website records. This takes a few seconds.`
              : lastCheck ? 'The last check found no outside providers. You can add the ones you know about.'
              : canEdit ? 'Run a check to find them from public records, or add the ones you know about.' : 'No check has been run for this vendor yet.'}
          </div>
        </div>
      ) : (
        <>
          {/* 2 Key numbers */}
          <div className="fp-kpis" style={{ ...card(isDark), padding: 0, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
            {[
              { label: 'Companies', value: activeParties.length, sub: `${activeProducts} product${activeProducts === 1 ? '' : 's'} and services`, color: ink },
              { label: 'Shared with your other vendors', value: sharedCount, sub: sharedCount ? 'Concentration risk across your portfolio' : 'None shared yet', color: sharedCount ? orange : ink,
                link: { text: 'View portfolio', go: () => navigate('/fourth-parties') } },
              { label: 'Receive data or payments', value: dataCount, sub: 'Email, analytics, payments, support or business apps', color: ink,
                click: () => { setTab('active'); setCategory(null); } },
            ].map((k, i) => (
              <div key={k.label} style={{ padding: '14px 18px', borderRight: i < 2 ? `1px solid ${line2}` : 'none', minWidth: 0 }}>
                <div style={{ fontSize: 12, color: muted }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: k.color, marginTop: 4 }}>{k.value}</div>
                <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>
                  {k.sub}{k.link && <> · <button style={{ ...linkBtn(isDark), fontSize: 12 }} onClick={k.link.go}>{k.link.text}</button></>}
                </div>
              </div>
            ))}
          </div>

          {/* 3 What it depends on */}
          {categoryCounts.length > 0 && (
            <div style={card(isDark)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: ink }}>What it depends on</span>
                <span style={{ fontSize: 12, color: muted }}>companies by category · click one to filter</span>
                {category && <button style={{ ...linkBtn(isDark), marginLeft: 'auto' }} onClick={() => setCategory(null)}>Clear filter</button>}
              </div>
              <div style={{ display: 'flex', height: 12, borderRadius: 99, overflow: 'hidden', marginTop: 12, gap: 2 }} aria-hidden="true">
                {categoryCounts.map(([cat, n]) => (
                  <span key={cat} title={`${cat}: ${n}`} style={{ flex: n, background: categoryColor(cat), opacity: category && category !== cat ? 0.25 : 1, cursor: 'pointer' }}
                    onClick={() => setCategory(category === cat ? null : cat)} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                {categoryCounts.map(([cat, n]) => (
                  <button key={cat} onClick={() => { setCategory(category === cat ? null : cat); setTab('active'); }} aria-pressed={category === cat}
                    style={{ ...pill(categoryColor(cat)), padding: '5px 11px', border: `1px solid ${category === cat ? categoryColor(cat) : 'transparent'}`, cursor: 'pointer', fontFamily: sans }}>
                    {cat} {n}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 4 Check status */}
          {status && (status.important.length + status.optional.length) > 0 && (
            <div role="status" style={{ ...card(isDark), padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={pill(status.important.length ? (isDark ? '#FBBF24' : '#B45309') : muted)}>Partly checked</span>
              <div style={{ flex: 1, fontSize: 12, color: slate, lineHeight: 1.55 }}>
                {[...status.important, ...status.optional].join(' ')} Everything found earlier was kept.{canEdit && <> <button style={{ ...linkBtn(isDark), fontSize: 12 }} onClick={runCheck}>Check now</button> tries again.</>}
              </div>
              <button aria-label="Dismiss" onClick={() => setStatusDismissed(true)} style={{ ...btn('ghost'), padding: 2 }}>✕</button>
            </div>
          )}

          {/* 5 List */}
          <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: `1px solid ${line}`, flexWrap: 'wrap' }}>
              <input aria-label="Search fourth parties" placeholder="Search company, product or category" value={search}
                onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: 260, maxWidth: '100%', padding: '7px 10px', fontSize: 12.5 }} />
              <span style={{ display: 'inline-flex', border: `1px solid ${line}`, borderRadius: 8, overflow: 'hidden' }} role="tablist" aria-label="Status">
                {([['active', 'Active'], ['not_detected', 'No longer seen'], ['removed', 'Removed']] as [Tab, string][]).map(([k, l]) => (
                  <button key={k} role="tab" aria-selected={tab === k} style={segBtn(tab === k)} onClick={() => setTab(k)}>{l} {counts[k]}</button>
                ))}
              </span>
              <select aria-label="Category" value={category || ''} onChange={e => setCategory(e.target.value || null)}
                style={{ ...inputStyle, width: 'auto', padding: '7px 10px', fontSize: 12.5 }}>
                <option value="">All categories</option>
                {(data?.categories || []).filter(cat => parties.some(p => p.products.some(x => x.category === cat))).map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <select aria-label="Sort" value={sortKey} onChange={e => setSortKey(e.target.value as SortKey)}
                style={{ ...inputStyle, width: 'auto', padding: '7px 10px', fontSize: 12.5 }}>
                <option value="shared">Sort: Shared first</option><option value="products">Sort: Most products</option>
                <option value="newest">Sort: Newest first</option><option value="name">Sort: Name</option>
              </select>
              <span style={{ marginLeft: 'auto', display: 'inline-flex', border: `1px solid ${line}`, borderRadius: 8, overflow: 'hidden' }} role="tablist" aria-label="View">
                {([['company', 'By company'], ['category', 'By category'], ['map', 'Map']] as const).map(([k, l]) => (
                  <button key={k} role="tab" aria-selected={view === k} style={segBtn(view === k)} onClick={() => setView(k)}>{l}</button>
                ))}
              </span>
            </div>

            {visible.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', fontSize: 12.5, color: slate }}>
                {tab === 'active' && !search && !category ? 'No active fourth parties.' : 'Nothing matches.'}
                {(search || category) && <> <button style={linkBtn(isDark)} onClick={() => { setSearch(''); setCategory(null); }}>Clear filters</button></>}
              </div>
            ) : view === 'company' ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={thS}>Company</th><th style={thS}>Category</th><th style={thS}>Products</th><th style={thS}>Shared with</th>
                    <th style={thS}>First detected</th><th style={thS}>Source</th><th style={{ ...thS, width: 28 }} aria-label="Open" />
                  </tr></thead>
                  <tbody>
                    {visible.map(({ party, products }) => {
                      const cats = Array.from(new Set(products.map(p => p.category)));
                      const open = openDomain === party.company_domain;
                      return (
                        <tr key={party.company_domain} className="fp-row" tabIndex={0} onClick={() => setOpenDomain(party.company_domain)}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenDomain(party.company_domain); } }}
                          style={{ cursor: 'pointer', background: open ? (isDark ? '#0b1a2e' : '#F5F9FE') : 'transparent' }}>
                          <td style={tdS}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <CompanyLogo name={party.company_name} domain={party.company_domain} isDark={isDark} size={32} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: ink }}>{party.company_name}</div>
                                {party.company_domain !== party.company_name && <div style={{ fontSize: 11.5, color: muted }}>{party.company_domain}</div>}
                              </div>
                            </div>
                          </td>
                          <td style={tdS}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {cats.slice(0, 2).map(c => <span key={c} style={pill(categoryColor(c))}>{c}</span>)}
                              {cats.length > 2 && <span style={{ fontSize: 11.5, color: muted }}>+{cats.length - 2}</span>}
                            </div>
                          </td>
                          <td style={{ ...tdS, fontSize: 12.5, color: ink, maxWidth: 320 }}>
                            <b>{products.length}</b>{' '}
                            <span style={{ color: muted }}>{products.slice(0, 2).map(p => p.product).join(', ')}{products.length > 2 ? ` +${products.length - 2}` : ''}</span>
                          </td>
                          <td style={tdS}><SharedCell party={party} /></td>
                          <td style={{ ...tdS, fontSize: 12, color: slate, whiteSpace: 'nowrap' }}>
                            {fmtDate(party.first_detected)}{tab === 'active' && isNew(party.first_detected) && <> <span style={{ ...pill(accent), fontSize: 10, padding: '1px 6px' }}>NEW</span></>}
                          </td>
                          <td style={tdS}>{sourceTag(party, products)}</td>
                          <td style={{ ...tdS, color: muted, fontSize: 16 }}>›</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : view === 'category' ? (
              <div>
                {Array.from(new Set(visible.flatMap(v => v.products.map(p => p.category))))
                  .sort((a, b) => ((data?.categories || []).indexOf(a) + 1 || 99) - ((data?.categories || []).indexOf(b) + 1 || 99))
                  .filter(cat => !category || cat === category)
                  .map((cat, i) => {
                    const items = visible.filter(v => v.products.some(p => p.category === cat));
                    return (
                      <div key={cat} style={{ borderTop: i ? `1px solid ${line}` : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px 6px' }}>
                          <span style={pill(categoryColor(cat))}>{cat}</span><span style={{ fontSize: 12, color: muted }}>{items.length} compan{items.length === 1 ? 'y' : 'ies'}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8, padding: '4px 14px 14px' }}>
                          {items.map(({ party, products }) => (
                            <button key={party.company_domain} onClick={() => setOpenDomain(party.company_domain)}
                              style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1px solid ${openDomain === party.company_domain ? accent : line}`, borderRadius: 10,
                                padding: '10px 12px', background: surface, cursor: 'pointer', fontFamily: sans, textAlign: 'left', minWidth: 0 }}>
                              <CompanyLogo name={party.company_name} domain={party.company_domain} isDark={isDark} size={26} />
                              <span style={{ minWidth: 0, flex: 1 }}>
                                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: ink }}>{party.company_name}</span>
                                <span style={{ display: 'block', fontSize: 11.5, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {products.filter(p => p.category === cat).map(p => p.product).join(', ')}
                                </span>
                              </span>
                              {sharedOf(party).length > 0 && <span style={{ width: 8, height: 8, borderRadius: '50%', background: orange, flexShrink: 0 }} title="Shared with your other vendors" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <DependencyMap vendorName={vendorName} vendorDomain={null} items={visible} isDark={isDark} sharedOf={sharedOf}
                onOpen={d => setOpenDomain(d)} selected={openDomain} ink={ink} muted={muted} line={line} />
            )}
            <div style={{ padding: '10px 14px', fontSize: 11.5, color: muted, borderTop: `1px solid ${line2}` }}>
              Click a company to see its products, how each was detected, and its notes. Fourth parties are for information only and don't change {vendorName}'s risk score.
            </div>
          </div>
        </>
      )}

      {/* 6 Drawer */}
      {openParty && !editing && !removing && (
        <FourthPartyDrawer
          party={openParty} vendorName={vendorName} isDark={isDark}
          shared={sharedOf(openParty)} canEdit={canEdit} canRemove={canRemove}
          index={openIndex} total={visible.length}
          onPrev={openIndex > 0 ? () => setOpenDomain(visible[openIndex - 1].party.company_domain) : undefined}
          onNext={openIndex >= 0 && openIndex < visible.length - 1 ? () => setOpenDomain(visible[openIndex + 1].party.company_domain) : undefined}
          onClose={() => setOpenDomain(null)}
          evidenceOpen={evidenceOpen}
          toggleEvidence={id => setEvidenceOpen(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; })}
          onEdit={product => openDialog({ product, party: openParty })}
          onRemove={products => { setRemoving({ products, party: openParty }); setRemoveError(''); }}
          onRestore={restore}
          onOpenVendor={id => navigate(`/vendors/${id}`)}
          onAddVendor={() => navigate('/vendors/add', { state: { name: openParty.company_name, domain: openParty.company_domain } })}
          pill={pill} btn={btn} colors={{ ink, muted, slate, line, line2, surface, subtle, accent, accentBg, accentInk, danger, orange }}
        />
      )}

      {/* Add / edit dialog */}
      {editing !== null && (
        <div style={overlay} onMouseDown={event => { if (event.target === event.currentTarget && !saving) setEditing(null); }}>
          <div style={dialog} role="dialog" aria-modal="true" aria-label={editing === 'new' ? 'Add fourth party' : 'Edit fourth party'}>
            <div style={{ fontSize: 15, fontWeight: 700, color: ink }}>
              {editing === 'new' ? 'Add fourth party' : detectedEdit ? `Note on ${editing.product.product}` : 'Edit fourth party'}
            </div>
            <div style={{ fontSize: 12, color: slate, margin: '4px 0 14px', lineHeight: 1.5 }}>
              {editing === 'new'
                ? `Record a company ${vendorName} relies on that can't be seen from outside, such as a payroll provider or a data processor.`
                : detectedEdit
                  ? 'This entry was detected automatically, so only its note can be edited.'
                  : 'Update this manually added entry.'}
            </div>
            <form noValidate onSubmit={event => { event.preventDefault(); if (!saving) save(); }} style={{ display: 'grid', gap: 12 }}>
              {editing === 'new' && (
                <div style={{ position: 'relative' }}>
                  <label style={labelStyle} htmlFor="fp-search">Search known products (optional)</label>
                  <input id="fp-search" style={inputStyle} value={catalogQuery} placeholder="For example Stripe, Okta, Snowflake" autoFocus autoComplete="off"
                    onChange={event => setCatalogQuery(event.target.value)} />
                  {catalogResults.length > 0 && (
                    <div style={{ marginTop: 4, border: `1px solid ${line}`, borderRadius: 7, maxHeight: 180, overflowY: 'auto' }}>
                      {catalogResults.map(entry => (
                        <button type="button" key={`${entry.company_domain}-${entry.product}`} onClick={() => pickCatalogEntry(entry)}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '7px 10px', background: 'none', border: 'none', borderBottom: `1px solid ${line}`, cursor: 'pointer', fontFamily: sans }}>
                          <CompanyLogo name={entry.company_name} domain={entry.company_domain} isDark={isDark} size={18} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: ink }}>{entry.product}</span>
                          <span style={{ fontSize: 11, color: muted }}>· {entry.company_name} · {entry.category}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!detectedEdit && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={labelStyle} htmlFor="fp-company">Company</label>
                      <input id="fp-company" style={inputStyle} value={form.company_name} placeholder="Stripe" maxLength={200}
                        onChange={event => setForm(f => ({ ...f, company_name: event.target.value }))} />
                    </div>
                    <div>
                      <label style={labelStyle} htmlFor="fp-domain">Website domain</label>
                      <input id="fp-domain" style={inputStyle} value={form.company_domain} placeholder="stripe.com" maxLength={253}
                        onChange={event => setForm(f => ({ ...f, company_domain: event.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={labelStyle} htmlFor="fp-product">Product or service (optional)</label>
                      <input id="fp-product" style={inputStyle} value={form.product} placeholder="Stripe Payments" maxLength={200}
                        onChange={event => setForm(f => ({ ...f, product: event.target.value }))} />
                    </div>
                    <div>
                      <label style={labelStyle} htmlFor="fp-category">Category</label>
                      <select id="fp-category" style={inputStyle} value={form.category} onChange={event => setForm(f => ({ ...f, category: event.target.value }))}>
                        {(data?.categories || ['Other']).map(cat => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle} htmlFor="fp-description">What it's used for (optional)</label>
                    <input id="fp-description" style={inputStyle} value={form.description} placeholder="Processes card payments" maxLength={1000}
                      onChange={event => setForm(f => ({ ...f, description: event.target.value }))} />
                  </div>
                </>
              )}
              <div>
                <label style={labelStyle} htmlFor="fp-notes">Note{detectedEdit ? '' : ' (optional)'}</label>
                <textarea id="fp-notes" style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} value={form.notes} maxLength={2000}
                  placeholder="Where this came from, or anything your team should know" autoFocus={detectedEdit}
                  onChange={event => setForm(f => ({ ...f, notes: event.target.value }))} />
              </div>
              {formError && <div style={errorText} role="alert">{formError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                <button type="button" style={btn()} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
                <button type="submit" style={{ ...btn('primary'), opacity: saving ? 0.7 : 1 }} disabled={saving}>
                  {saving ? 'Saving…' : editing === 'new' ? 'Add fourth party' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove confirmation */}
      {removing && (
        <div style={overlay} onMouseDown={event => { if (event.target === event.currentTarget && !saving) setRemoving(null); }}>
          <div style={{ ...dialog, maxWidth: 440 }} role="dialog" aria-modal="true" aria-label="Remove fourth party">
            <div style={{ fontSize: 15, fontWeight: 700, color: ink, marginBottom: 6 }}>
              Remove {removing.products.length === 1 ? removing.products[0].product : `${removing.party.company_name} (${removing.products.length} products)`}?
            </div>
            <div style={{ fontSize: 13, color: slate, lineHeight: 1.5 }}>
              {removing.products.every(p => p.source === 'manual')
                ? `This deletes the entry from ${vendorName}'s fourth parties. This can't be undone.`
                : `It's taken off ${vendorName}'s list and future checks won't add it back. You can restore it from the Removed tab.`}
            </div>
            {removeError && <div style={{ ...errorText, marginTop: 10 }} role="alert">{removeError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={btn()} onClick={() => setRemoving(null)} disabled={saving}>Cancel</button>
              <button style={{ ...btn('primary'), background: T.red, borderColor: T.red, opacity: saving ? 0.7 : 1 }} onClick={confirmRemove} disabled={saving}>
                {saving ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Drawer: one company, everything about it ───────────────────────── */
function FourthPartyDrawer({ party, vendorName, isDark, shared, canEdit, canRemove, index, total, onPrev, onNext, onClose,
  evidenceOpen, toggleEvidence, onEdit, onRemove, onRestore, onOpenVendor, onAddVendor, pill, btn, colors: c }: {
  party: FourthParty; vendorName: string; isDark: boolean; shared: { id: string; name: string; domain: string }[];
  canEdit: boolean; canRemove: boolean; index: number; total: number; onPrev?: () => void; onNext?: () => void; onClose: () => void;
  evidenceOpen: Set<string>; toggleEvidence: (id: string) => void; onEdit: (p: Product) => void; onRemove: (p: Product[]) => void;
  onRestore: (p: Product) => void; onOpenVendor: (id: string) => void; onAddVendor: () => void;
  pill: (color: string) => React.CSSProperties; btn: (kind?: 'default' | 'primary' | 'ghost' | 'danger') => React.CSSProperties; colors: any;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (ref.current && !ref.current.contains(t) && !t.closest('.fp-row, [role="dialog"], button')) onClose();
    };
    document.addEventListener('keydown', onKey);
    const timer = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); clearTimeout(timer); };
  }, [onClose, onPrev, onNext]);

  const order: Record<string, number> = { active: 0, not_detected: 1, removed: 2 };
  const products = [...party.products].sort((a, b) => order[a.status] - order[b.status] || a.product.localeCompare(b.product));
  const active = products.filter(p => p.status === 'active');
  const cats = Array.from(new Set(active.map(p => p.category)));
  const status = active.length ? { label: 'Active', color: '#16A34A' } : products.some(p => p.status === 'not_detected')
    ? { label: 'No longer seen', color: isDark ? '#FBBF24' : '#B45309' } : { label: 'Removed', color: c.muted };
  const uses = Array.from(new Set(active.map(p => (p.description || '').trim()).filter(Boolean)));
  const allEvidenceOpen = products.filter(p => p.evidence.length).every(p => evidenceOpen.has(p.id));
  const tile = (label: string, value: React.ReactNode, bg: string, fg: string, sub: string) => (
    <div style={{ background: bg, borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 11.5, color: sub }}>{label}</div>
      <div style={{ fontSize: typeof value === 'number' ? 21 : 14, fontWeight: 700, color: fg, marginTop: typeof value === 'number' ? 0 : 4 }}>{value}</div>
    </div>
  );
  const h: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: c.ink, margin: '18px 0 8px', display: 'flex', alignItems: 'center' };

  return (
    <div ref={ref} role="dialog" aria-modal="false" aria-label={party.company_name} style={{
      position: 'fixed', top: 64, right: 0, bottom: 0, width: 'min(480px, 100vw)', zIndex: 1300, background: c.surface,
      borderLeft: `1px solid ${c.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans, animation: 'fpDrawerIn .2s ease-out',
    }}>
      <div style={{ padding: '18px 20px', borderBottom: `1px solid ${c.line}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CompanyLogo name={party.company_name} domain={party.company_domain} isDark={isDark} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 17, fontWeight: 700, color: c.ink }}>{party.company_name}</span>
              <span style={pill(status.color)}>● {status.label}</span>
            </div>
            <a href={`https://${party.company_domain}`} target="_blank" rel="noreferrer noopener" style={{ fontSize: 12, color: c.accent, textDecoration: 'none' }}>{party.company_domain} ↗</a>
          </div>
          {total > 1 && index >= 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button aria-label="Previous company" style={{ ...btn(), padding: '3px 9px', opacity: onPrev ? 1 : 0.4 }} disabled={!onPrev} onClick={onPrev}>‹</button>
              <span style={{ fontSize: 11.5, color: c.muted, minWidth: 44, textAlign: 'center' }}>{index + 1} of {total}</span>
              <button aria-label="Next company" style={{ ...btn(), padding: '3px 9px', opacity: onNext ? 1 : 0.4 }} disabled={!onNext} onClick={onNext}>›</button>
            </span>
          )}
          <button aria-label="Close" onClick={onClose} style={{ ...btn('ghost'), padding: 4 }}>✕</button>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
          {cats.map(cat => <span key={cat} style={pill(categoryColor(cat))}>{cat}</span>)}
          {shared.length > 0 && <span style={pill(c.orange)}>Shared with {shared.length} vendor{shared.length === 1 ? '' : 's'}</span>}
          {party.monitored_vendor && <span style={pill(c.accent)}>Also your vendor</span>}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8, marginTop: 16 }}>
          {tile('Your vendors using it', shared.length, isDark ? 'rgba(234,88,12,.14)' : '#FFF7ED', c.orange, isDark ? '#FDBA74' : '#9A3412')}
          {tile('Products used', active.length, c.accentBg, c.accent, c.accentInk)}
          {tile('First detected', fmtDate(party.first_detected), c.subtle, c.ink, c.muted)}
        </div>

        <div style={h}>What it does for {vendorName}</div>
        <div style={{ fontSize: 12.5, color: uses.length ? c.slate : c.muted, lineHeight: 1.6 }}>
          {uses.length ? uses.slice(0, 3).join(' · ') : `${party.company_name} provides ${active.map(p => p.product).slice(0, 3).join(', ') || 'a service'} to ${vendorName}.`}
        </div>

        <div style={h}>
          Products and services ({products.length})
          {products.some(p => p.evidence.length) && (
            <button style={{ ...linkBtn(isDark), marginLeft: 'auto', fontSize: 12 }}
              onClick={() => products.filter(p => p.evidence.length && (allEvidenceOpen ? evidenceOpen.has(p.id) : !evidenceOpen.has(p.id))).forEach(p => toggleEvidence(p.id))}>
              {allEvidenceOpen ? 'Hide evidence' : 'Show evidence for all'}
            </button>
          )}
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          {products.map(p => {
            const open = evidenceOpen.has(p.id);
            const inactive = p.status !== 'active';
            return (
              <div key={p.id} style={{ border: `1px solid ${c.line}`, borderRadius: 10, padding: '10px 12px', opacity: inactive ? 0.75 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', cursor: p.evidence.length ? 'pointer' : 'default' }}
                  onClick={() => p.evidence.length && toggleEvidence(p.id)}>
                  <b style={{ fontSize: 13, color: c.ink }}>{p.product}</b>
                  <span style={{ fontSize: 11.5, color: c.muted }}>{p.category}</span>
                  {p.status === 'active' && isNew(p.first_detected) && <span style={{ ...pill(c.accent), fontSize: 10, padding: '1px 6px' }}>NEW</span>}
                  {p.status === 'not_detected' && <span style={pill(isDark ? '#FBBF24' : '#B45309')}>No longer seen</span>}
                  {p.status === 'removed' && <span style={pill(c.muted)}>Removed</span>}
                  {p.source === 'manual' && <span style={pill('#7C3AED')}>Added manually</span>}
                  {p.evidence.length > 0 && <span style={{ marginLeft: 'auto', fontSize: 11, color: c.muted }}>{open ? '▾' : '▸'}</span>}
                </div>
                {p.description && <div style={{ fontSize: 12, color: c.slate, marginTop: 4, lineHeight: 1.5 }}>{p.description}</div>}
                {open && p.evidence.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {p.evidence.map(ev => (
                      <span key={ev} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: c.subtle, color: c.slate, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', overflowWrap: 'anywhere' }}>{ev}</span>
                    ))}
                  </div>
                )}
                {p.notes && (
                  <div style={{ fontSize: 12, color: c.ink, marginTop: 8, padding: '6px 9px', borderRadius: 6, background: c.subtle, lineHeight: 1.5 }}>
                    <span style={{ color: c.muted }}>Note: </span>{p.notes}
                  </div>
                )}
                {(canEdit || canRemove) && (
                  <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
                    {canEdit && p.status !== 'removed' && (
                      <button style={{ ...linkBtn(isDark), fontSize: 12 }} onClick={() => onEdit(p)}>{p.source === 'manual' ? 'Edit' : p.notes ? 'Edit note' : 'Add note'}</button>
                    )}
                    {canRemove && p.status !== 'removed' && <button style={{ ...linkBtn(isDark), fontSize: 12, color: c.danger }} onClick={() => onRemove([p])}>Remove</button>}
                    {canEdit && p.status === 'removed' && <button style={{ ...linkBtn(isDark), fontSize: 12 }} onClick={() => onRestore(p)}>Restore</button>}
                  </div>
                )}
                <div style={{ fontSize: 11, color: c.muted, marginTop: 6 }}>
                  {p.first_detected ? `First seen ${fmtDate(p.first_detected)}` : ''}{p.last_seen ? ` · last seen ${fmtDate(p.last_seen)}` : ''}
                </div>
              </div>
            );
          })}
        </div>

        <div style={h}>Also used by your vendors</div>
        {shared.length === 0 ? (
          <div style={{ fontSize: 12.5, color: c.muted }}>None of your other vendors use {party.company_name}.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {shared.map(v => (
              <button key={v.id} onClick={() => onOpenVendor(v.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${c.line}`, borderRadius: 8, padding: '8px 10px', background: c.surface, cursor: 'pointer', fontFamily: sans, textAlign: 'left' }}>
                <CompanyLogo name={v.name} domain={v.domain} isDark={isDark} size={22} />
                <span style={{ fontSize: 12.5, color: c.ink, flex: 1 }}>{v.name}</span>
                <span style={{ fontSize: 12, color: c.accent }}>Open ›</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <a href={`https://${party.company_domain}`} target="_blank" rel="noreferrer noopener" style={{ ...btn('primary'), textDecoration: 'none' }}>Visit website ↗</a>
        {party.monitored_vendor
          ? <button style={btn()} onClick={() => onOpenVendor(party.monitored_vendor!.id)}>Open vendor</button>
          : canEdit && <button style={btn()} onClick={onAddVendor}>Add as vendor</button>}
        {canRemove && active.length > 0 && <button style={{ ...btn('danger'), marginLeft: 'auto' }} onClick={() => onRemove(active)}>Remove</button>}
      </div>
    </div>
  );
}

/* ── Map view: the vendor in the middle, its fourth parties around it ── */
function DependencyMap({ vendorName, items, isDark, sharedOf, onOpen, selected, ink, muted, line }: {
  vendorName: string; vendorDomain: string | null; items: { party: FourthParty; products: Product[] }[]; isDark: boolean;
  sharedOf: (p: FourthParty) => { id: string }[]; onOpen: (domain: string) => void; selected: string | null; ink: string; muted: string; line: string;
}) {
  const W = 900, H = 520, cx = W / 2, cy = H / 2;
  const shown = items.slice(0, 40);
  const inner = shown.slice(0, 14), outer = shown.slice(14);
  const place = (list: typeof shown, radius: number, offset: number) => list.map((it, i) => {
    const a = offset + (i / Math.max(list.length, 1)) * Math.PI * 2;
    return { ...it, x: cx + Math.cos(a) * radius * 1.45, y: cy + Math.sin(a) * radius };
  });
  const nodes = [...place(inner, 165, -Math.PI / 2), ...place(outer, 235, -Math.PI / 2 + 0.2)];
  return (
    <div style={{ padding: 14 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`${vendorName} and its ${shown.length} fourth parties`} style={{ display: 'block' }}>
        {nodes.map(n => <line key={`l-${n.party.company_domain}`} x1={cx} y1={cy} x2={n.x} y2={n.y} stroke={line} strokeWidth={1.2} />)}
        <circle cx={cx} cy={cy} r={42} fill={isDark ? '#0c2a4a' : '#E6F1FB'} stroke="#185FA5" strokeWidth={2} />
        <text x={cx} y={cy + 4} textAnchor="middle" fontSize={13} fontWeight={700} fill={isDark ? '#BFDBFE' : '#0C447C'} fontFamily={sans}>
          {vendorName.length > 14 ? `${vendorName.slice(0, 13)}…` : vendorName}
        </text>
        {nodes.map(n => {
          const r = Math.min(30, 17 + n.products.length * 2.5);
          const cat = n.products[0]?.category || 'Other';
          const isShared = sharedOf(n.party).length > 0;
          const on = selected === n.party.company_domain;
          return (
            <g key={n.party.company_domain} style={{ cursor: 'pointer' }} onClick={() => onOpen(n.party.company_domain)}>
              <title>{`${n.party.company_name} · ${n.products.length} product${n.products.length === 1 ? '' : 's'}${isShared ? ' · shared with your other vendors' : ''}`}</title>
              <circle cx={n.x} cy={n.y} r={r} fill={isDark ? '#111' : '#fff'} stroke={isShared ? '#EA580C' : on ? '#185FA5' : categoryColor(cat)} strokeWidth={isShared || on ? 3 : 2} />
              <image href={`https://www.google.com/s2/favicons?domain=${n.party.company_domain}&sz=64`} x={n.x - 10} y={n.y - 10} width={20} height={20} />
              <text x={n.x} y={n.y + r + 13} textAnchor="middle" fontSize={11} fill={ink} fontFamily={sans}>
                {n.party.company_name.length > 16 ? `${n.party.company_name.slice(0, 15)}…` : n.party.company_name}
              </text>
            </g>
          );
        })}
      </svg>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11.5, color: muted, marginTop: 6 }}>
        <span>Circle size = number of products</span><span>Ring colour = category</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', border: '2.5px solid #EA580C', verticalAlign: -1 }} /> Shared with your other vendors</span>
        {items.length > shown.length && <span>Showing 40 of {items.length}; use search or a category to narrow it.</span>}
      </div>
    </div>
  );
}
